import { adminService } from "@/api/admin/admin.service";
import type { TAdminAuditEvent, TAuditStats } from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TDateRangeValue,
  type TFilterDef,
} from "@/components/data-grid";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import { extractErrorMessage } from "@/utils";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const PAGE_SIZE = 20;

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

export function AuditLogsPage() {
  const [reloadTick, setReloadTick] = useState(0);
  const [stats, setStats] = useState<TAuditStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const loadStats = useCallback(async (signal: AbortSignal) => {
    setStatsLoading(true);
    setStatsError(null);
    const { data, error: err } = await adminService.getAuditStats(undefined, { signal });
    if (signal.aborted) return;
    if (err) {
      setStatsError(await extractErrorMessage(err));
    } else if (data) {
      setStats(data.data ?? null);
    }
    setStatsLoading(false);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadStats(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats, reloadTick]);

  const fetcher = async (query: TDataGridQuery) => {
    const dateRange = (query.filters.created as TDateRangeValue | undefined) ?? {};
    const eventType = asString(query.filters.event_type);
    const response = await adminService.searchAuditLogs(
      {
        limit: query.limit,
        offset: query.offset,
        ...(asString(query.filters.user_id) ? { user_id: asString(query.filters.user_id) } : {}),
        ...(eventType ? { event_types: [eventType] } : {}),
        ...(asString(query.filters.target_entity)
          ? { target_entity: asString(query.filters.target_entity) }
          : {}),
        ...(dateRange.from ? { date_from: dateRange.from } : {}),
        ...(dateRange.to ? { date_to: dateRange.to } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    // Backend returns { events, total_count, limit, offset }; normalizeListResponse
    // handles the `events` list key.
    return response.data;
  };

  // NOTE: the audit /search endpoint does NOT accept sort_by/sort_order — it
  // always returns newest-first. Columns are therefore intentionally NOT
  // sortable; do not add `sortable: true` to these columns.
  const columns: TColumnDef<TAdminAuditEvent>[] = [
    { id: "event_type", header: "Event", type: "badge" },
    { id: "target_entity", header: "Entity", type: "text", accessor: (e) => e.target_entity ?? "" },
    {
      id: "target_id",
      header: "Target ID",
      type: "uuid",
      hideable: true,
      accessor: (e) => e.target_id ?? "",
    },
    {
      id: "user_id",
      header: "User",
      type: "uuid",
      hideable: true,
      accessor: (e) => e.user_id ?? "",
    },
    { id: "timestamp", header: "Time", type: "date" },
    {
      id: "action_details",
      header: "Details",
      type: "json",
      hideable: true,
      defaultHidden: true,
      accessor: (e) => e.action_details ?? null,
    },
  ];

  const filters: TFilterDef[] = [
    { id: "user_id", type: "user", label: "User", placeholder: "Search by name or email…" },
    { id: "event_type", type: "text", label: "Event type", placeholder: "e.g. user_updated" },
    { id: "target_entity", type: "text", label: "Entity type", placeholder: "e.g. user" },
    { id: "created", type: "dateRange", label: "Date" },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Audit Logs</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              System-wide audit trail of administrative and security events
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setReloadTick((t) => t + 1)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        {statsLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : statsError ? (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {statsError}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard title="Total Events" value={String(stats.total_events)} />
            <StatCard title="Unique Users" value={String(stats.unique_users)} />
            <StatCard
              title="Event Types"
              value={String(Object.keys(stats.event_counts_by_type).length)}
            />
            <StatCard
              title="Entities"
              value={String(Object.keys(stats.event_counts_by_entity).length)}
            />
          </div>
        ) : null}

        <DataGrid<TAdminAuditEvent>
          getRowId={(row) => row.event_id ?? `${row.timestamp}-${row.event_type}`}
          persistKey="admin.audit-logs"
          ariaLabel="Audit logs"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
          }}
          details={{
            variant: "drawer",
            title: (row) => row.event_type,
            render: (row) => (
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Time</dt>
                  <dd className="text-foreground">{fmtDate(row.timestamp)}</dd>
                </div>
                {row.target_entity && (
                  <div>
                    <dt className="text-muted-foreground">Entity</dt>
                    <dd className="text-foreground">
                      {row.target_entity}
                      {row.target_id ? ` · ${row.target_id}` : ""}
                    </dd>
                  </div>
                )}
                {row.user_id && (
                  <div>
                    <dt className="text-muted-foreground">User</dt>
                    <dd className="font-mono text-xs text-foreground">{row.user_id}</dd>
                  </div>
                )}
                {row.ip_address && (
                  <div>
                    <dt className="text-muted-foreground">IP</dt>
                    <dd className="text-foreground">{row.ip_address}</dd>
                  </div>
                )}
                {row.user_agent && (
                  <div>
                    <dt className="text-muted-foreground">Agent</dt>
                    <dd className="break-all text-foreground">{row.user_agent}</dd>
                  </div>
                )}
                {row.correlation_id && (
                  <div>
                    <dt className="text-muted-foreground">Correlation</dt>
                    <dd className="font-mono text-xs text-foreground">{row.correlation_id}</dd>
                  </div>
                )}
                {row.action_details && (
                  <div>
                    <dt className="text-muted-foreground">Action details</dt>
                    <dd>
                      <pre className="mt-1 max-h-72 overflow-auto rounded bg-muted p-2 text-xs text-muted-foreground">
                        {JSON.stringify(row.action_details, null, 2)}
                      </pre>
                    </dd>
                  </div>
                )}
              </dl>
            ),
          }}
          emptyState={{ title: "No audit events found." }}
        />
      </div>
    </div>
  );
}
